/**
 * Keeps TelemetryDeck login/logout signals in sync with auth state.
 *
 * We intentionally do NOT set clientUser to the account userId — TelemetryDeck
 * stays on the per-browser anonymous ID so individuals cannot be identified or
 * tracked across devices. Use event payloads (e.g. visitorState, role) for funnels.
 *
 * LOGIN_SUCCESS requires an access token so registration / pending-verification
 * stubs (user without a session) do not inflate login counts.
 *
 * Must be rendered inside <AuthProvider>. Renders nothing.
 */

import { useEffect, useRef } from 'react';

import { EVENTS } from '@constants/telemetryEvents';
import { useAuth } from '@contexts/AuthContext';
import { signal } from '@utils/telemetry';

export function TelemetryUserSync() {
  const { user, token } = useAuth();
  const prevSessionUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const sessionUserId = user?.userId && token ? user.userId : null;

    if (sessionUserId && user) {
      if (prevSessionUserIdRef.current === null) {
        void signal(EVENTS.LOGIN_SUCCESS, { role: user.role });
      }
    } else if (prevSessionUserIdRef.current !== null) {
      void signal(EVENTS.LOGOUT);
    }

    prevSessionUserIdRef.current = sessionUserId;
  }, [user, token]);

  return null;
}
